/**
 * SEQUENTIAL SAME-APPROVER COLLAPSE — client-side DISPLAY convenience, never a
 * gate. Mirrors `server/helpers/approvalStepCollapse.js`'s pure decision so
 * the To Approve list, the order-detail stepper and the workflow editor can
 * all say the same thing about what one press will do — but the SERVER is the
 * sole authority on what actually collapses at Approve time
 * (`POST /api/approvals/orders/:orderId/approve`).
 *
 * Owner ruling, 2026-08-18: *"Crewforge handles duplicate removals by
 * approving all duplicates that are sequential in one step until it hits a
 * new approval user."* Collapsing CLICKS, never RECORDS — every step still
 * exists, still gets its own row, its own audit line. The 2026-08-11 ruling
 * (a buyer named mid-chain AND again at the end is TWO REAL STEPS) is
 * unaffected: those two are not SEQUENTIAL, so a run of either is length 1.
 *
 * 🚨 MATCH ON `approver_user_id` ONLY, NEVER `approver_name` — duplicate
 * person-names and even multiple user rows for one human exist in this app.
 * 🚨 FAILS CLOSED on a null/undefined `approver_user_id` on EITHER side —
 * `null === null` is `true` in JS, and two legacy steps with no bound user
 * must never be treated as "the same person".
 */

/** Sort steps ascending by `step_order`, without mutating the input. */
export function sortStepsByOrder(steps) {
  return (steps || []).slice().sort((a, b) => Number(a.step_order) - Number(b.step_order));
}

/**
 * Group a step list into consecutive runs (by step_order) that share the
 * same, non-null `approver_user_id`. A step with a null/undefined id is
 * always its own run of one — see the file docblock.
 *
 * @param {Array<{step_order:number, approver_user_id:(number|null|undefined), approver_name?:string}>} steps
 * @returns {Array<{approver_user_id:(number|null|undefined), approver_name:?string, steps:Array<Object>}>}
 */
export function groupSequentialSameApproverRuns(steps) {
  const sorted = sortStepsByOrder(steps);
  const runs = [];
  for (const step of sorted) {
    const last = runs[runs.length - 1];
    const approverId = step.approver_user_id;
    const canJoin = last != null
      && approverId != null
      && last.approver_user_id != null
      && approverId === last.approver_user_id;
    if (canJoin) {
      last.steps.push(step);
    } else {
      runs.push({ approver_user_id: approverId, approver_name: step.approver_name, steps: [step] });
    }
  }
  return runs;
}

/**
 * Given every step of ONE approval and the `step_order` currently being
 * decided, return the run starting there (mirrors the server's
 * `collapseSequentialSameApproverSteps`). Always at least length 1.
 */
export function collapseSequentialSameApproverSteps(steps, fromStepOrder) {
  const runs = groupSequentialSameApproverRuns(steps);
  const run = runs.find((r) => r.steps.some((s) => Number(s.step_order) === Number(fromStepOrder)));
  if (!run) return [];
  // Only the PORTION of the run at-or-after fromStepOrder is what one press
  // from here decides (a run's later steps could in principle be queried
  // starting mid-run, though callers of this file today only ever start at
  // the CURRENT step).
  return run.steps.filter((s) => Number(s.step_order) >= Number(fromStepOrder));
}

/**
 * The count of sequential same-approver steps starting at `fromStepOrder`,
 * for display. Returns 1 when there is nothing to collapse (including when
 * `fromStepOrder` cannot be found at all — fails to "no collapse" rather
 * than an error, since this only ever drives button copy).
 */
export function collapsibleStepCountFrom(steps, fromStepOrder) {
  const run = collapseSequentialSameApproverSteps(steps, fromStepOrder);
  return run.length > 0 ? run.length : 1;
}

/**
 * For the workflow EDITOR (`ApprovalWorkflowManager.js`): the longest run of
 * sequential same-approver steps ANYWHERE in the workflow (length > 1 only),
 * or `null` if the workflow has no such run. Used for the WARN-not-BLOCK
 * banner beside a workflow's summary line.
 */
export function findLongestSequentialSameApproverRun(steps) {
  const runs = groupSequentialSameApproverRuns(steps).filter((r) => r.steps.length > 1);
  if (runs.length === 0) return null;
  return runs.reduce((best, r) => (r.steps.length > best.steps.length ? r : best), runs[0]);
}

/**
 * THE button label for an Approve control, given how many sequential steps
 * one press would decide. `collapsibleStepCount` should come from the
 * server (`order.collapsible_step_count` on `GET /api/approvals/pending`, or
 * computed here via `collapsibleStepCountFrom` against a fetched `steps`
 * array, e.g. `GET /api/approvals/orders/:orderId`).
 *
 * `opts.isViewerNamedApprover` / `opts.approverName` (both optional, only
 * consulted when `collapsibleStepCount > 1`): "your steps" is only an
 * accurate claim when the VIEWER is the step's named approver. On
 * `client/src/pages/Approvals.js` that is always true by construction — the
 * To Approve list's own server query filters to orders where the viewer IS
 * the current step's `approver_user_id` — so it never needs to pass these.
 * `ApprovalStatus.js` is different: its `isCurrentApprover` gates actions on
 * `currentStep.approver_user_id === userId || isAdmin` alone — no
 * department-capability disjunct — so a GLOBAL ADMIN viewing SOMEONE ELSE's
 * run is the only case reachable there where "Approve all 3 of your steps"
 * would misattribute three of Jake's steps to the admin pressing the button
 * (2026-08-18 adversarial review, M3). Pass `isViewerNamedApprover: false`
 * with the step's `approverName` in that case to get "Approve all 3 of Jake
 * Dimmer's steps" instead.
 * Omitting `opts` (or passing neither field) keeps the "your steps" default,
 * so every EXISTING 2-argument call site is unaffected.
 */
export function approveButtonLabel(collapsibleStepCount, isHiddenStep, opts = {}) {
  const count = Number(collapsibleStepCount);
  if (Number.isFinite(count) && count > 1) {
    if (opts.isViewerNamedApprover === false && opts.approverName) {
      return `Approve all ${count} of ${opts.approverName}'s steps`;
    }
    return `Approve all ${count} of your steps`;
  }
  return isHiddenStep ? 'Confirm & Place Order' : 'Approve';
}
